import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  deleteNarrativeTemplates,
  readNarratives,
  readNarrativeTemplates,
  shareNarrativeTemplatesWithClientGroup,
  shareNarrativeTemplatesWithUsers,
  updateNarrativeTemplate,
} from '@api/narratives';
import {
  Button,
  CloseIcon,
  GridRightThinIcon,
  HelpPlainIcon,
  IconButtonV2,
  LoaderSkeleton,
  MeatballsIcon,
  ModalV2 as Modal,
  OverlayPopover,
  PencilIcon,
  Popover,
  ShareIcon,
  Tabs,
  toast,
  TopicLibraryCard,
  TrashIcon,
} from '@utilities';
import ModalShare from '../../../../components/ModalShare';
import NarrativesTemplatesModal from '../../../../components/NarrativesTemplatesModal';

import { getTileIcon } from '../../../Narratives/utilities/helpers';
import { getDeleteTemplateErrorMessage, getTemplateTopicIds } from '../../utilities/helpers';

import styles from './_index.module.scss';

const TEMPLATE_TABS = [
  { label: 'Numerator', value: 'numerator' },
  { label: 'Custom', value: 'custom' },
];

const hasPersistedTemplateId = (templateId) =>
  templateId !== null &&
  templateId !== undefined &&
  !String(templateId).startsWith('temp-template-');

const NarrativesTemplatesWindow = ({
  customTemplatesRefreshKey = 0,
  initialActiveTab = 'numerator',
  initialActiveTemplateId = null,
  onCloseToolWindow,
  onSelectTemplate,
}) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [isLoadingNumerator, setIsLoadingNumerator] = useState(true);
  const [isLoadingCustom, setIsLoadingCustom] = useState(true);
  const [numeratorTemplates, setNumeratorTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [activeActionsTemplateId, setActiveActionsTemplateId] = useState(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedName, setEditedName] = useState('');
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [shareTemplateId, setShareTemplateId] = useState(null);
  const actionButtonRefs = useRef({});
  const handledCustomTemplatesRefreshKeyRef = useRef(customTemplatesRefreshKey);

  const fetchCustomTemplates = useCallback(async ({ shouldShowLoader = true } = {}) => {
    try {
      if (shouldShowLoader) {
        setIsLoadingCustom(true);
      }
      const response = await readNarrativeTemplates();
      setCustomTemplates(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error(error);
      toast.error('Unable to load custom templates. Please refresh and try again.');
    } finally {
      setIsLoadingCustom(false);
    }
  }, []);

  useEffect(() => {
    const fetchNarrativeRoutes = async () => {
      try {
        const response = await readNarratives();
        setNumeratorTemplates(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error(error);
        toast.error('Unable to load Numerator templates. Please refresh and try again.');
      } finally {
        setIsLoadingNumerator(false);
      }
    };

    fetchNarrativeRoutes();
  }, []);

  useEffect(() => {
    fetchCustomTemplates();
  }, [fetchCustomTemplates]);

  useEffect(() => {
    if (activeTab !== 'custom') return;
    if (customTemplatesRefreshKey === 0) {
      handledCustomTemplatesRefreshKeyRef.current = 0;
      return;
    }
    if (customTemplatesRefreshKey === handledCustomTemplatesRefreshKeyRef.current) return;

    handledCustomTemplatesRefreshKeyRef.current = customTemplatesRefreshKey;
    fetchCustomTemplates({ shouldShowLoader: false });
  }, [activeTab, customTemplatesRefreshKey, fetchCustomTemplates]);

  useEffect(() => {
    setActiveActionsTemplateId(null);
    setDeleteTemplateId(null);
    setEditingTemplate(null);
    setShareTemplateId(null);
  }, [activeTab]);

  const templates = useMemo(
    () => (activeTab === 'custom' ? customTemplates : numeratorTemplates),
    [activeTab, customTemplates, numeratorTemplates]
  );

  const isLoading = activeTab === 'custom' ? isLoadingCustom : isLoadingNumerator;

  useEffect(() => {
    setActiveTab(initialActiveTab === 'custom' ? 'custom' : 'numerator');
  }, [initialActiveTab]);

  useEffect(() => {
    if (!initialActiveTemplateId) return;
    const element = document.getElementById(`template-card-${initialActiveTemplateId}`);
    if (!element || typeof element.scrollIntoView !== 'function') return;
    element.scrollIntoView({ block: 'center' });
  }, [activeTab, initialActiveTemplateId, isLoading]);

  const handleTemplateClick = async (template) => {
    if (!template) return;

    if (typeof onSelectTemplate !== 'function') return;
    await onSelectTemplate({
      activeTab,
      template,
    });
  };

  const handleActionButtonClick = (event, templateId) => {
    event.stopPropagation();
    setActiveActionsTemplateId((currentId) => (currentId === templateId ? null : templateId));
  };

  const handleOpenEditModal = (event, template) => {
    event.stopPropagation();
    setActiveActionsTemplateId(null);
    setEditingTemplate(template);
    setEditedDescription(template?.description || '');
    setEditedName(template?.name || '');
  };

  const handleOpenDeleteModal = (event, templateId) => {
    event.stopPropagation();
    setActiveActionsTemplateId(null);
    setDeleteTemplateId(templateId);
  };

  const handleOpenShareModal = (event, templateId) => {
    event.stopPropagation();
    setActiveActionsTemplateId(null);
    setShareTemplateId(templateId);
  };

  const handleSaveTemplate = async () => {
    if (!hasPersistedTemplateId(editingTemplate?.id)) return;

    const normalizedName = editedName.trim();

    if (!normalizedName) return;

    try {
      setIsSavingTemplate(true);
      await updateNarrativeTemplate({
        description: editedDescription.trim(),
        name: normalizedName,
        templateId: editingTemplate.id,
        topicIds: getTemplateTopicIds(editingTemplate),
      });

      setCustomTemplates((previousTemplates) =>
        previousTemplates.map((template) =>
          template?.id === editingTemplate.id
            ? {
                ...template,
                description: editedDescription.trim(),
                name: normalizedName,
              }
            : template
        )
      );
      setEditingTemplate(null);
      toast.success('Template details updated.');
    } catch (error) {
      console.error(error);
      toast.error('Unable to update template. Please try again.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!hasPersistedTemplateId(deleteTemplateId)) return;

    try {
      setIsDeletingTemplate(true);
      await deleteNarrativeTemplates({ ids: [deleteTemplateId] });
      setCustomTemplates((previousTemplates) =>
        previousTemplates.filter((template) => template?.id !== deleteTemplateId)
      );
      setDeleteTemplateId(null);
      toast.success('Template deleted.');
    } catch (error) {
      console.error(error);
      toast.error(getDeleteTemplateErrorMessage(error));
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const renderLoadingCards = (cardsToRender) =>
    Array.from({ length: cardsToRender }, (_, index) => (
      <TopicLibraryCard
        className={styles['narratives-templates-card']}
        key={`loading-template-card-${index}`}
        tabIndex={-1}
        variant="dark"
      >
        <LoaderSkeleton className={styles['narratives-templates-loader']} height={56}>
          <rect height="18" rx="4" ry="4" width="60%" x="0" y="0" />
          <rect height="14" rx="4" ry="4" width="100%" x="0" y="26" />
          <rect height="14" rx="4" ry="4" width="82%" x="0" y="42" />
        </LoaderSkeleton>
      </TopicLibraryCard>
    ));

  return (
    <div className={styles['narratives-templates-window']}>
      <div className={styles['narratives-templates-header-row']}>
        <div className={styles['narratives-templates-header']}>Browse Template Library</div>
        {onCloseToolWindow ? (
          <IconButtonV2
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onCloseToolWindow}
            size="medium"
            tooltip="Close"
            tooltipProps={{ placement: 'left' }}
            variant="default"
          />
        ) : null}
      </div>

      <div className={styles['narratives-templates-tabs']}>
        <Tabs
          className={styles['narratives-templates-tabs-control']}
          onChange={setActiveTab}
          options={TEMPLATE_TABS}
          size="small"
          value={activeTab}
          variant="rounded"
        />
      </div>

      <div className={styles['narratives-templates-cards']}>
        <h4 className={styles['narratives-templates-list-title']}>
          {activeTab === 'custom' ? 'Custom Templates' : 'Numerator Templates'}
        </h4>

        {isLoading && renderLoadingCards(6)}

        {!isLoading && templates.length === 0 && (
          <div className={styles['narratives-templates-empty']}>No templates available.</div>
        )}

        {!isLoading &&
          templates.map((template) => {
            const canShowActions = hasPersistedTemplateId(template?.id);
            const baseIcon =
              activeTab === 'custom' ? (
                <GridRightThinIcon />
              ) : (
                getTileIcon({ narrativeId: template.id }).icon
              );
            const icon = <div className={styles['narratives-templates-icon-slot']}>{baseIcon}</div>;

            return (
              <TopicLibraryCard
                className={styles['narratives-templates-card']}
                data-testid={`template-card-${template.id}`}
                icon={icon}
                id={`template-card-${template.id}`}
                key={template.id}
                onClick={() => handleTemplateClick(template)}
                title={template.name || 'Untitled Template'}
                titleAccessory={
                  activeTab === 'custom' && canShowActions ? (
                    <div
                      className={styles['narratives-templates-actions']}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <IconButtonV2
                        aria-label="More Actions"
                        className={styles['narratives-templates-meatball']}
                        data-testid={`template-card-more-actions-${template.id}`}
                        icon={<MeatballsIcon />}
                        onClick={(event) => handleActionButtonClick(event, template.id)}
                        ref={(element) => {
                          actionButtonRefs.current[template.id] = element;
                        }}
                        size="small"
                        variant="subtle"
                      />
                      <OverlayPopover
                        hasArrow
                        isActive={activeActionsTemplateId === template.id}
                        onHide={() => setActiveActionsTemplateId(null)}
                        placement="bottom"
                        target={actionButtonRefs.current[template.id]}
                      >
                        <Popover.Menu>
                          {template?.isOwner !== false ? (
                            <Popover.Item
                              aria-label="Edit Template Details"
                              data-testid={`template-card-edit-${template.id}`}
                              icon={<PencilIcon />}
                              onClick={(event) => handleOpenEditModal(event, template)}
                            >
                              <p>Edit details</p>
                            </Popover.Item>
                          ) : null}
                          <Popover.Item
                            aria-label="Share Template"
                            data-testid={`template-card-share-${template.id}`}
                            icon={<ShareIcon />}
                            onClick={(event) => handleOpenShareModal(event, template.id)}
                          >
                            <p>Share</p>
                          </Popover.Item>
                          <Popover.Item
                            aria-label="Delete Template"
                            data-testid={`template-card-delete-${template.id}`}
                            icon={<TrashIcon />}
                            onClick={(event) => handleOpenDeleteModal(event, template.id)}
                          >
                            <p>Delete</p>
                          </Popover.Item>
                        </Popover.Menu>
                      </OverlayPopover>
                    </div>
                  ) : activeTab === 'numerator' && canShowActions ? (
                    <div
                      className={styles['narratives-templates-actions']}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <IconButtonV2
                        aria-label="Help"
                        className={styles['narratives-templates-meatball']}
                        data-testid={`template-card-help-${template.id}`}
                        icon={<HelpPlainIcon />}
                        onClick={() =>
                          window.open(
                            'https://help.numerator.com/en/collections/15743024-narratives',
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                        size="small"
                        tooltip="Help"
                        tooltipProps={{ placement: 'left' }}
                        variant="subtle"
                      />
                    </div>
                  ) : null
                }
                variant="dark"
              >
                <p className={styles['narratives-templates-description']}>
                  {template.description || 'No description'}
                </p>
              </TopicLibraryCard>
            );
          })}
      </div>

      <NarrativesTemplatesModal
        contentClassName={styles['narratives-templates-modal-fields']}
        description={editedDescription}
        descriptionId="edit-template-description"
        formMode="edit"
        isActive={Boolean(editingTemplate)}
        isSubmitDisabled={!editedName.trim()}
        isSubmitting={isSavingTemplate}
        onClose={() => setEditingTemplate(null)}
        onDescriptionChange={setEditedDescription}
        onSubmit={handleSaveTemplate}
        onTemplateNameChange={(value) => setEditedName(String(value))}
        templateName={editedName}
        templateNameId="edit-template-name"
        title="Edit template details"
      />
      {hasPersistedTemplateId(shareTemplateId) ? (
        <ModalShare
          hasSharePublicly={false}
          ids={[shareTemplateId]}
          setShowModalShare={(isOpen) => {
            if (!isOpen) setShareTemplateId(null);
          }}
          shareItemWithClients={shareNarrativeTemplatesWithClientGroup}
          shareItemWithUsers={shareNarrativeTemplatesWithUsers}
        />
      ) : null}

      <Modal
        buttons={[
          <Button
            data-testid="template-delete-modal-cancel"
            key="cancel-delete-template"
            onClick={() => setDeleteTemplateId(null)}
            text="Cancel"
            variant="secondary"
          />,
          <Button
            data-testid="template-delete-modal-confirm"
            isDisabled={isDeletingTemplate}
            key="confirm-delete-template"
            onClick={handleDeleteTemplate}
            text="Delete"
          />,
        ]}
        isActive={hasPersistedTemplateId(deleteTemplateId)}
        onClose={() => setDeleteTemplateId(null)}
        title="Delete template"
      >
        Are you sure you want to delete this template?
      </Modal>
    </div>
  );
};

NarrativesTemplatesWindow.propTypes = {
  customTemplatesRefreshKey: PropTypes.number,
  initialActiveTab: PropTypes.oneOf(['custom', 'numerator']),
  initialActiveTemplateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onCloseToolWindow: PropTypes.func,
  onSelectTemplate: PropTypes.func,
};

export default NarrativesTemplatesWindow;
